/**
 * useTestResultInsights
 *
 * Reads the patient's test results and produces a `ResultInterpretation`
 * per recognized biomarker. This hook is deliberately narrow after
 * issue #1593: overview cards need only value + unit + a category
 * (for the range-bar zone in the detail sheet). All clinical framing
 * (thresholds, source citations, disclaimer) lives in the tapped
 * detail sheet, not here.
 *
 * ────────────────────────────────────────────────────────────────────────
 * MEDICAL-REVIEW STATUS
 * ────────────────────────────────────────────────────────────────────────
 * The category thresholds below are anchored to published guidelines
 * (see per-function citations). They must be signed off by Louise's
 * medical advisor before the app treats them as production-safe. Copy
 * that patients see under "Typical range" lives in
 * `src/packages/test-results/content/biomarker-factsheets.ts` — that's
 * where sourced ranges get surfaced.
 *
 * Priority order for category:
 *   1. `result.clinicalAssessment` from the API (backend threshold
 *      logic — the source of truth when set).
 *   2. Client-side thresholds below, only when context is available
 *      and the backend hasn't classified the row.
 *   3. Otherwise: no interpretation (the biomarker still renders as
 *      raw value + unit via the calling surface).
 *
 * Primary sources referenced by the client-side thresholds:
 *   - ASRM 2020 committee opinion on ovarian reserve testing
 *     https://www.fertstert.org/article/S0015-0282(20)32336-0/fulltext
 *   - La Marca et al. 2011 age-nomogram (Fertil Steril)
 *     https://www.fertstert.org/article/S0015-0282(11)00370-0/fulltext
 *   - ESHRE Bologna criteria for poor ovarian response
 *     https://academic.oup.com/humrep/article/26/7/1616/2913872
 *   - WHO Laboratory Manual, 6th edition (2021)
 *     https://www.who.int/publications/i/item/9789240030787
 *   - ESHRE 2019 Ovarian Stimulation for IVF/ICSI guideline
 *     https://academic.oup.com/hropen/article/2020/2/hoaa009/5827574
 *   - Labarta et al. 2017 serum P4 on FET day (Hum Reprod)
 *     https://academic.oup.com/humrep/article/32/12/2437/4600228
 *   - ASRM 2021 luteal phase deficiency committee opinion
 *     https://www.asrm.org/practice-guidance/practice-committee-documents/diagnosis-and-treatment-of-luteal-phase-deciency-a-committee-opinion-2021/
 */

import { useMemo } from "react";
import {
  useTestResults,
  type TestResult,
} from "@src/packages/test-results/hooks/useTestResults";
import { useActiveCycleWithPhases } from "@src/packages/cycles/hooks/useCycles";
import { useTranslations } from "@src/packages/i18n";
import {
  getTestResultInterpretationContext,
  resolveResultInterpretationContext,
  type ResultInterpretationContext,
} from "@src/packages/test-results/interpretation-context";

// Semen-fluid analysis rows carry concentration / totalMotility /
// morphology / volume fields on the wire that aren't part of the base
// TestResult type because they're SA-specific. We extend locally and
// read defensively rather than casting through `any` / `unknown`.
type SemenAnalysisRow = TestResult & {
  concentration?: string | number | null;
  count?: string | number | null;
  totalMotility?: string | number | null;
  motility?: string | number | null;
  morphology?: string | number | null;
  volume?: string | number | null;
};

export type ResultCategory = "normal" | "low" | "high" | "borderline";

/** Phase types relevant to progesterone / E2 interpretation. */
export type ProgesteronePhaseContext =
  | "pre_treatment"
  | "protocol"
  | "egg_retrieval"
  | "fertilization"
  | "embryo_development"
  | "transfer"
  | "luteal_phase"
  | null
  | undefined;

export type ResultInterpretation = {
  testName: string;
  /** One-liner describing what the test measures (not interpreting the
   *  user's value). Same string regardless of category. Used as the
   *  subtitle on the home result card. */
  testPurpose: string;
  value: string;
  unit: string;
  category: ResultCategory;
  /** Where the category came from. "api" = server clinicalAssessment
   *  (backend threshold logic — the source of truth when available).
   *  "client" = derived on the frontend from published-guideline
   *  thresholds. */
  categorySource?: "api" | "client";
  /** Source `TestResult.id` — lets callers look up the raw row when
   *  they need to open a detail sheet (which needs id/date/history).
   *  See `TestResultInsightCardWithDetail` for the wrapper that uses
   *  this to construct a `LabResult` for `LabResultDetailSheet`. */
  sourceResultId?: string;
  /** The `TestResult.date` of the interpreted result. */
  sourceResultDate?: string | null;
  /** Cycle and treatment timing used by the interpretation, when known. */
  interpretationContext?: ResultInterpretationContext;
  /* --------------------------------------------------------------
   * The fields below are legacy copy fields used by
   * `HomepageReviewConceptHome` fixtures and dev previews. The hook
   * does NOT populate them any more — clinical framing lives in the
   * detail sheet and the biomarker factsheet, not here. Kept optional
   * so existing callers compile until they are cleaned up.
   * -------------------------------------------------------------- */
  headline?: string;
  interpretation?: string;
  whatThisMeans?: string;
  nextStep?: string;
};

/**
 * A React key that is unique per interpreted result.
 *
 * `testName` is not one. Serial monitoring is the normal case — oestradiol, LH
 * and AMH are each measured on several days of one stimulation — so a list keyed
 * by the test's name collides on every repeat measurement. React's own warning
 * spells out the consequence: children "may be duplicated and/or omitted", so a
 * result she has recorded can silently vanish from the list rather than fail
 * loudly.
 *
 * `sourceResultId` is the row's own `TestResult.id` and is the stable answer
 * whenever the API supplied it. The fallback carries the date so repeats are
 * distinguishable, and the index last so uniqueness is guaranteed even for two
 * measurements of one test on one day — which the record permits.
 */
export function resultInterpretationKey(
  interpretation: Pick<
    ResultInterpretation,
    "testName" | "sourceResultId" | "sourceResultDate"
  >,
  index: number,
): string {
  if (interpretation.sourceResultId) return interpretation.sourceResultId;
  return `${interpretation.testName}|${interpretation.sourceResultDate ?? ""}|${index}`;
}

export type TestResultInsightsQueryState = {
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  onRetry: () => void;
};

/**
 * Map the API's clinicalAssessment string onto our category type.
 * Backend is the source of truth for this classification. Returns
 * null when the backend hasn't assessed the row, so callers can fall
 * back to client-side thresholds.
 */
export function categoryFromClinicalAssessment(
  assessment: TestResult["clinicalAssessment"],
): ResultCategory | null {
  if (assessment === "low") return "low";
  if (assessment === "normal") return "normal";
  if (assessment === "high") return "high";
  return null; // "unknown" or null
}

// -------------------------------------------------------------
// Unit normalizations
// -------------------------------------------------------------

/** 1 ng/mL ≈ 3.18 nmol/L */
function normalizeProgesteroneToNgPerMl(value: number, unit: string): number {
  const u = unit.trim().toLowerCase();
  if (u.includes("nmol")) return value / 3.18;
  return value;
}

/** pmol/L → pg/mL: divide by 3.67 */
function normalizeE2ToPgPerMl(value: number, unit: string): number {
  const u = unit.trim().toLowerCase();
  if (u.includes("pmol")) return value / 3.67;
  return value;
}

// -------------------------------------------------------------
// Pure categorizers — value → category | null. Return null when
// context is insufficient (issue #1593 point 4: don't invent
// defaults when we can't be sure).
// -------------------------------------------------------------

/** AMH — ASRM 2020 / La Marca 2011. Cycle-day independent, so always
 *  yields a category when the biomarker matches. Age-adjusted at the
 *  1.0–1.5 borderline zone. */
function amhCategorize(value: number, age: number | undefined): ResultCategory {
  const isYoung = age !== undefined && age < 35;
  if (value >= 3.5) return "high";
  if (value >= 1.5) return "normal";
  if (value >= 1.0) return isYoung ? "borderline" : "normal";
  return "low";
}

/** FSH baseline (cycle days 2–4) — ESHRE Bologna 2011. */
function fshBaselineCategorize(value: number): ResultCategory {
  if (value < 10) return "normal";
  if (value <= 15) return "borderline";
  return "high";
}

/** FSH on days 5–35 — population reference-range comparison, NOT a
 *  DOR assessment. Ranges from lab reference tables (Mayo, LabCorp).
 *  Outside days 5–35 returns null. */
function fshByDayCategorize(
  value: number,
  cycleDay: number,
): ResultCategory | null {
  let low: number;
  let high: number;
  if (cycleDay >= 5 && cycleDay <= 13) {
    low = 3;
    high = 10;
  } else if (cycleDay >= 14 && cycleDay <= 16) {
    low = 5;
    high = 20;
  } else if (cycleDay >= 17 && cycleDay <= 35) {
    low = 1.5;
    high = 8;
  } else {
    return null;
  }
  if (value < low) return "low";
  if (value > high) return "high";
  return "normal";
}

/** Progesterone — cycle-phase aware. Luteal (transfer / luteal_phase)
 *  vs baseline. Returns null when phase is unknown. */
function progesteroneCategorize(
  value: number,
  phase: ProgesteronePhaseContext,
): ResultCategory | null {
  if (!phase) return null;
  const inLuteal = phase === "transfer" || phase === "luteal_phase";
  if (inLuteal) {
    if (value < 5) return "low";
    if (value < 10) return "borderline";
    return "normal";
  }
  // Follicular / pre-treatment / stim baseline.
  if (value < 1) return "normal";
  if (value < 3) return "borderline";
  return "high";
}

/** Estradiol — context-aware (baseline vs stim). Returns null when
 *  context is unknown (mid-cycle peak, luteal, etc.). */
type E2Context =
  | { kind: "baseline"; cycleDay: number }
  | { kind: "stim" }
  | { kind: "unknown" };

function e2Categorize(value: number, ctx: E2Context): ResultCategory | null {
  if (ctx.kind === "baseline") {
    return value >= 80 ? "high" : "normal";
  }
  if (ctx.kind === "stim") {
    if (value < 200) return "low";
    if (value > 4000) return "high";
    return "normal";
  }
  return null;
}

/** LH — cycle-day aware. Returns null when cycle day is unknown or
 *  outside recognized windows. */
function lhByDayCategorize(
  value: number,
  cycleDay: number,
): ResultCategory | null {
  if (cycleDay >= 2 && cycleDay <= 4) {
    if (value >= 15) return "high";
    if (value < 1) return "low";
    return "normal";
  }
  if (cycleDay >= 12 && cycleDay <= 16) {
    return value >= 15 ? "normal" : "low";
  }
  if (cycleDay >= 17 && cycleDay <= 35) {
    return value >= 15 ? "high" : "normal";
  }
  return null;
}

/** Semen analysis — WHO 6th edition (2021) lower reference limits. */
function saCategorize(results: {
  count?: number;
  motility?: number;
  morphology?: number;
  volume?: number;
}): ResultCategory {
  let issues = 0;
  if (results.count !== undefined && results.count < 16) issues++;
  if (results.motility !== undefined && results.motility < 42) issues++;
  if (results.morphology !== undefined && results.morphology < 4) issues++;
  if (results.volume !== undefined && results.volume < 1.4) issues++;
  if (issues === 0) return "normal";
  if (issues === 1) return "borderline";
  return "low";
}

// -------------------------------------------------------------
// The hook
// -------------------------------------------------------------

/**
 * Interprets test results and returns a `ResultInterpretation` per
 * recognized biomarker. Self-sufficient: pulls active-cycle context
 * internally via `useActiveCycleWithPhases()` so callers don't have
 * to wire phase/cycle-start-date.
 */
export function useTestResultInsights(userAge?: number): {
  interpretations: ResultInterpretation[];
  hasResults: boolean;
  queryState: TestResultInsightsQueryState;
} {
  const testResultsQuery = useTestResults();
  const { data: testResults } = testResultsQuery;
  const activeCycle = useActiveCycleWithPhases();
  const t = useTranslations();

  const cycleStartDate = activeCycle.cycle?.startDate ?? null;
  const activeCycleId = activeCycle.cycle?.id ?? null;

  const interpretations = useMemo(() => {
    const results: ResultInterpretation[] = [];
    if (!testResults || !Array.isArray(testResults)) return results;

    for (const result of testResults) {
      const name = (result.name ?? "").toLowerCase();
      const numericValue = parseFloat(
        String(result.patientValue ?? result.value ?? ""),
      );
      if (isNaN(numericValue)) continue;

      const apiCategory = categoryFromClinicalAssessment(
        result.clinicalAssessment,
      );

      const push = (params: {
        testPurposeKey: string;
        testNameKey: string;
        category: ResultCategory;
        categorySource: "api" | "client";
        defaultUnit: string;
        displayValue?: string;
        interpretationContext?: ResultInterpretationContext;
      }) => {
        results.push({
          testName: t(params.testNameKey),
          testPurpose: t(params.testPurposeKey),
          value: params.displayValue ?? String(numericValue),
          unit: result.unit ?? params.defaultUnit,
          category: params.category,
          categorySource: params.categorySource,
          sourceResultId: result.id,
          sourceResultDate: result.date,
          interpretationContext: params.interpretationContext,
        });
      };

      // Results with a different cycle ID belong to historical context that
      // the active-cycle hook cannot resolve. Unlinked results can still use
      // the active cycle when their date falls inside it.
      const belongsToActiveCycle =
        !result.cycleId || !activeCycleId || result.cycleId === activeCycleId;
      const baseInterpretationContext = resolveResultInterpretationContext({
        resultDate: result.date,
        cycleStartDate: belongsToActiveCycle ? cycleStartDate : null,
        phaseId: belongsToActiveCycle ? result.phaseId : null,
        phases: belongsToActiveCycle ? (activeCycle.phases ?? []) : [],
      });

      // AMH — always safe to categorize (cycle-day independent).
      if (name.includes("amh") || name.includes("anti-mullerian")) {
        const clientCategory = amhCategorize(numericValue, userAge);
        push({
          testPurposeKey: "test_result_insights.amh.test_purpose",
          testNameKey: "test_result_insights.amh.test_name",
          category: apiCategory ?? clientCategory,
          categorySource: apiCategory ? "api" : "client",
          defaultUnit: "ng/mL",
        });
        continue;
      }

      // FSH — Bologna DOR on cycle day 2–4 only. Outside that
      // window we use a population reference-range comparison.
      // If cycle day is unknown AND API didn't assess, skip (issue
      // #1593 point 4: don't invent day-3 default).
      if (
        name.includes("fsh") ||
        name.includes("follicle stimulating") ||
        name.includes("follicle-stimulating")
      ) {
        const cycleDay = baseInterpretationContext.cycleDay;
        let clientCategory: ResultCategory | null = null;
        if (cycleDay !== null) {
          if (cycleDay >= 2 && cycleDay <= 4) {
            clientCategory = fshBaselineCategorize(numericValue);
          } else {
            clientCategory = fshByDayCategorize(numericValue, cycleDay);
          }
        }
        const finalCategory = apiCategory ?? clientCategory;
        if (finalCategory === null) continue;
        push({
          testPurposeKey: "test_result_insights.fsh.test_purpose",
          testNameKey: "test_result_insights.fsh.test_name",
          category: finalCategory,
          categorySource: apiCategory ? "api" : "client",
          defaultUnit: "mIU/mL",
          interpretationContext: getTestResultInterpretationContext(
            result.name ?? "FSH",
            baseInterpretationContext,
          ),
        });
        continue;
      }

      // Progesterone — phase-dependent. Skip when phase is
      // unknown AND API didn't assess.
      if (name.includes("progesterone")) {
        const ngPerMl = normalizeProgesteroneToNgPerMl(
          numericValue,
          result.unit ?? "",
        );
        const interpretationContext = getTestResultInterpretationContext(
          result.name ?? "Progesterone",
          baseInterpretationContext,
        );
        const clientCategory = progesteroneCategorize(
          ngPerMl,
          interpretationContext.treatmentPhase as ProgesteronePhaseContext,
        );
        const finalCategory = apiCategory ?? clientCategory;
        if (finalCategory === null) continue;
        push({
          testPurposeKey: "test_result_insights.progesterone.test_purpose",
          testNameKey: "test_result_insights.progesterone.test_name",
          category: finalCategory,
          categorySource: apiCategory ? "api" : "client",
          defaultUnit: "ng/mL",
          interpretationContext,
        });
        continue;
      }

      // Estradiol (E2) — context-aware.
      if (
        name.includes("estradiol") ||
        name.includes("oestradiol") ||
        /\be2\b/.test(name)
      ) {
        const cycleDay = baseInterpretationContext.cycleDay;
        const pgPerMl = normalizeE2ToPgPerMl(numericValue, result.unit ?? "");
        const interpretationContext = getTestResultInterpretationContext(
          result.name ?? "Estradiol",
          baseInterpretationContext,
        );
        const ctx: E2Context =
          interpretationContext.treatmentPhase === "protocol"
            ? { kind: "stim" }
            : cycleDay !== null && cycleDay >= 2 && cycleDay <= 4
              ? { kind: "baseline", cycleDay }
              : { kind: "unknown" };
        const clientCategory = e2Categorize(pgPerMl, ctx);
        const finalCategory = apiCategory ?? clientCategory;
        if (finalCategory === null) continue;
        push({
          testPurposeKey: "test_result_insights.e2.test_purpose",
          testNameKey: "test_result_insights.e2.test_name",
          category: finalCategory,
          categorySource: apiCategory ? "api" : "client",
          defaultUnit: "pg/mL",
          interpretationContext,
        });
        continue;
      }

      // LH — cycle-day aware.
      if (
        /\blh\b/.test(name) ||
        name.includes("luteinizing") ||
        name.includes("luteinising")
      ) {
        const cycleDay = baseInterpretationContext.cycleDay;
        const interpretationContext = getTestResultInterpretationContext(
          result.name ?? "LH",
          baseInterpretationContext,
        );
        const clientCategory =
          cycleDay !== null ? lhByDayCategorize(numericValue, cycleDay) : null;
        const finalCategory = apiCategory ?? clientCategory;
        if (finalCategory === null) continue;
        push({
          testPurposeKey: "test_result_insights.lh.test_purpose",
          testNameKey: "test_result_insights.lh.test_name",
          category: finalCategory,
          categorySource: apiCategory ? "api" : "client",
          defaultUnit: "mIU/mL",
          interpretationContext,
        });
        continue;
      }
    }

    // Semen analysis. Aggregate multi-parameter row.
    const saResults = (testResults as SemenAnalysisRow[]).filter(
      (r): r is SemenAnalysisRow => r.type === "semenFluidAnalysis",
    );
    if (saResults.length > 0) {
      const sa = saResults[0]!;
      const saData = {
        count: parseFloat(String(sa.concentration ?? sa.count ?? "")),
        motility: parseFloat(String(sa.totalMotility ?? sa.motility ?? "")),
        morphology: parseFloat(String(sa.morphology ?? "")),
        volume: parseFloat(String(sa.volume ?? "")),
      };
      const hasAnyField = Object.values(saData).some((v) => !isNaN(v));
      if (hasAnyField) {
        const cleanData: {
          count?: number;
          motility?: number;
          morphology?: number;
          volume?: number;
        } = {};
        for (const [k, v] of Object.entries(saData)) {
          if (!isNaN(v)) {
            (cleanData as Record<string, number>)[k] = v;
          }
        }
        const apiSaCategory = categoryFromClinicalAssessment(
          sa.clinicalAssessment,
        );
        const clientCategory = saCategorize(cleanData);
        const finalCategory = apiSaCategory ?? clientCategory;
        results.push({
          testName: t("test_result_insights.sa.test_name"),
          testPurpose: t("test_result_insights.sa.test_purpose"),
          value: t("test_result_insights.sa.value_see_details"),
          unit: "",
          category: finalCategory,
          categorySource: apiSaCategory ? "api" : "client",
          sourceResultId: sa.id,
          sourceResultDate: sa.date,
        });
      }
    }

    return results;
  }, [
    testResults,
    userAge,
    cycleStartDate,
    activeCycleId,
    activeCycle.phases,
    t,
  ]);

  return {
    interpretations,
    hasResults: interpretations.length > 0,
    queryState: {
      // A query with data remains usable while a background refresh runs. Only
      // the initial no-data request should replace the Testing card with a
      // loading state.
      isLoading: testResultsQuery.isLoading,
      isError: testResultsQuery.isError && testResults === undefined,
      isFetching: testResultsQuery.isFetching,
      onRetry: () => {
        void testResultsQuery.refetch();
      },
    },
  };
}
